import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { RagStreamNotSupportedError } from "@/lib/api/rag-client";
import { getThread, sendMessage, sendMessageStream } from "../api/rag-api.js";
import {
  createOptimisticUserMessage,
  createRagErrorMessage,
  mapAssistantFromSendResponse,
  mapChatMessage,
  mapSourcesToCitations,
} from "../api/mappers.js";
import type { ApiChatSource, RagRetrievalMode, RagStreamStatus } from "../api/types.js";
import type { RagMessage } from "../types.js";

export interface UseRagChatSubmitParams {
  chatId: string;
  messages: RagMessage[];
  setMessages: Dispatch<SetStateAction<RagMessage[]>>;
  onFirstMessageTitle?: (title: string) => void;
  retrievalMode?: RagRetrievalMode;
}

export interface UseRagChatSubmitResult {
  handleSubmit: (message: string) => Promise<void>;
  /** Re-run the last user message without duplicating the user bubble. */
  handleRegenerate: () => Promise<void>;
  /** True from submit until the stream completes or errors. */
  isSubmitting: boolean;
  /** Progress steps reported by the RAG stream; empty when idle. */
  statusSteps: RagStreamStatus[];
}

function mergeStatusStep(steps: RagStreamStatus[], next: RagStreamStatus): RagStreamStatus[] {
  const last = steps[steps.length - 1];
  if (last && last.stage === next.stage) {
    return [...steps.slice(0, -1), { ...last, ...next }];
  }
  return [...steps, next];
}

function createStreamingAssistantId(): string {
  return `streaming-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function lastUserContent(messages: RagMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].role === "user" && messages[i].content.trim()) {
      return messages[i].content;
    }
  }
  return null;
}

function withoutTrailingAssistants(messages: RagMessage[]): RagMessage[] {
  const next = [...messages];
  while (next.length > 0 && next[next.length - 1].role === "assistant") {
    next.pop();
  }
  return next;
}

export function useRagChatSubmit({
  chatId,
  messages,
  setMessages,
  onFirstMessageTitle,
  retrievalMode,
}: UseRagChatSubmitParams): UseRagChatSubmitResult {
  const abortRef = useRef<AbortController | null>(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusSteps, setStatusSteps] = useState<RagStreamStatus[]>([]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const applyNonStreamingFallback = useCallback(
    async (
      message: string,
      options: { optimisticUserId?: string; isFirstMessage: boolean },
    ) => {
      const response = await sendMessage(chatId, message, { retrievalMode });
      const assistantMessage = mapAssistantFromSendResponse(response);
      const persistedUser = mapChatMessage(response.userMessage);

      setMessages((prev) => {
        const withUser = options.optimisticUserId
          ? prev.map((entry) =>
              entry.id === options.optimisticUserId ? { ...persistedUser } : entry,
            )
          : prev;
        return [...withoutTrailingAssistants(withUser), assistantMessage];
      });

      if (options.isFirstMessage) {
        const { thread } = await getThread(chatId);
        onFirstMessageTitle?.(thread.title);
      }
    },
    [chatId, onFirstMessageTitle, retrievalMode, setMessages],
  );

  const runStream = useCallback(
    async (
      message: string,
      options: {
        optimisticUserId?: string;
        isFirstMessage: boolean;
        /** When regenerating, strip trailing assistants before streaming. */
        stripTrailingAssistants?: boolean;
      },
    ) => {
      const assistantId = createStreamingAssistantId();

      if (options.stripTrailingAssistants) {
        setMessages((prev) => withoutTrailingAssistants(prev));
      }

      setIsSubmitting(true);
      setStatusSteps([]);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        await sendMessageStream(
          chatId,
          message,
          {
            onUser: (userMessage) => {
              if (!options.optimisticUserId) return;
              const persistedUser = mapChatMessage(userMessage);
              setMessages((prev) =>
                prev.map((entry) =>
                  entry.id === options.optimisticUserId ? { ...persistedUser } : entry,
                ),
              );
            },
            onStatus: (status) => {
              setStatusSteps((prev) => mergeStatusStep(prev, status));
            },
            onSources: (sources: ApiChatSource[]) => {
              const citations = mapSourcesToCitations(sources);
              setMessages((prev) =>
                prev.map((entry) =>
                  entry.id === assistantId ? { ...entry, citations } : entry,
                ),
              );
            },
            onDelta: (chunk) => {
              setMessages((prev) => {
                const existing = prev.find((entry) => entry.id === assistantId);
                if (!existing) {
                  return [
                    ...prev,
                    {
                      id: assistantId,
                      role: "assistant",
                      content: chunk,
                    },
                  ];
                }

                return prev.map((entry) =>
                  entry.id === assistantId
                    ? { ...entry, content: entry.content + chunk }
                    : entry,
                );
              });
            },
            onDone: (assistantMessage) => {
              const finalMessage = mapChatMessage(assistantMessage);
              setMessages((prev) => {
                if (prev.some((entry) => entry.id === assistantId)) {
                  return prev.map((entry) =>
                    entry.id === assistantId ? finalMessage : entry,
                  );
                }
                return [...prev, finalMessage];
              });
              setIsSubmitting(false);
              setStatusSteps([]);
            },
          },
          { signal: controller.signal, retrievalMode },
        );

        if (options.isFirstMessage) {
          const { thread } = await getThread(chatId);
          onFirstMessageTitle?.(thread.title);
        }
      } catch (error) {
        setStatusSteps([]);

        if (controller.signal.aborted) {
          setIsSubmitting(false);
          return;
        }

        setIsSubmitting(false);
        if (error instanceof RagStreamNotSupportedError) {
          setIsSubmitting(true);
          try {
            await applyNonStreamingFallback(message, {
              optimisticUserId: options.optimisticUserId,
              isFirstMessage: options.isFirstMessage,
            });
          } finally {
            setIsSubmitting(false);
          }
          return;
        }

        setMessages((prev) => [...prev, createRagErrorMessage()]);
      }
    },
    [applyNonStreamingFallback, chatId, onFirstMessageTitle, retrievalMode, setMessages],
  );

  const handleSubmit = useCallback(
    async (message: string) => {
      const isFirstMessage = messagesRef.current.length === 0;
      const optimisticUser = createOptimisticUserMessage(message);
      setMessages((prev) => [...prev, optimisticUser]);
      await runStream(message, {
        optimisticUserId: optimisticUser.id,
        isFirstMessage,
      });
    },
    [runStream, setMessages],
  );

  const handleRegenerate = useCallback(async () => {
    if (isSubmitting) return;
    const content = lastUserContent(messagesRef.current);
    if (!content) return;
    await runStream(content, {
      isFirstMessage: false,
      stripTrailingAssistants: true,
    });
  }, [isSubmitting, runStream]);

  return { handleSubmit, handleRegenerate, isSubmitting, statusSteps };
}
